import React, { useState } from 'react';
import { Container, Title, Tabs, Paper, Stack, Group, Button, Text, Alert, Modal } from '@mantine/core';
import { IconPlus, IconList, IconInfoCircle } from '@tabler/icons-react';
import { useBlocks, useCreateBlock, useUpdateBlock, useDeleteBlock } from '@/hooks/api/useBlocks';
import BlockStats from '@/components/organisms/BlockStats';
import BlockList from '@/components/organisms/BlockList';
import BlockForm from '@/components/forms/BlockForm';

/**
 * Coordinator Block Management Page
 * 
 * Provides coordinators with the ability to view and manage time blocks
 * for scheduling purposes. Coordinators can view block statistics, list
 * all blocks, and create new blocks as needed.
 * 
 * @returns JSX.Element
 */
const BlockManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [showForm, setShowForm] = useState<boolean>(false);
  const [selectedBlock, setSelectedBlock] = useState<any>(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all' as 'all' | 'active' | 'inactive',
    sortBy: 'name' as 'name' | 'startDate' | 'endDate' | 'createdAt',
    sortOrder: 'asc' as 'asc' | 'desc',
    page: 1,
    limit: 10,
  });

  // Data fetching hooks
  const { data: blocksData, isLoading: blocksLoading, error: blocksError } = useBlocks();
  
  // Debug logging
  console.log('📦 BlockManagement - blocksData:', blocksData);
  console.log('📦 BlockManagement - blocksData?.blocks:', blocksData?.blocks);
  console.log('📦 BlockManagement - blocksData?.data:', blocksData?.data);
  console.log('📦 BlockManagement - blocksData?.data?.blocks:', blocksData?.data?.blocks);
  
  // Calculate stats from blocks data and transform to BlockWithStats
  const rawBlocks = blocksData?.blocks || blocksData?.data?.blocks || [];
  console.log('📦 BlockManagement - rawBlocks:', rawBlocks, 'length:', rawBlocks.length);
  
  const blocks = rawBlocks.map((block: any) => ({
    ...block,
    assignmentCount: block._count?.assignments || block.assignmentCount || 0
  }));
  console.log('📦 BlockManagement - blocks after transform:', blocks, 'length:', blocks.length);
  
  // Calculate dynamic stats
  const now = new Date();
  const statsData = Array.isArray(blocks) ? (() => {
    const upcomingBlocks = blocks.filter((block: any) => 
      block.isActive && new Date(block.startDate) > now
    ).length;
    
    const expiredBlocks = blocks.filter((block: any) => 
      new Date(block.endDate) < now
    ).length;
    
    // Group blocks by month
    const blocksByMonthMap: { [key: string]: number } = {};
    blocks.forEach((block: any) => {
      const startDate = new Date(block.startDate);
      const monthKey = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`;
      blocksByMonthMap[monthKey] = (blocksByMonthMap[monthKey] || 0) + 1;
    });
    
    const blocksByMonth = Object.entries(blocksByMonthMap)
      .map(([month, count]) => ({ month, count }))
      .sort((a, b) => a.month.localeCompare(b.month));
    
    return {
      totalBlocks: blocks.length,
      activeBlocks: blocks.filter((block: any) => block.isActive).length,
      inactiveBlocks: blocks.filter((block: any) => !block.isActive).length,
      blocksByMonth,
      upcomingBlocks,
      expiredBlocks,
    };
  })() : null;
  const statsLoading = blocksLoading;
  const statsError = blocksError;
  
  // Mutation hooks
  const createBlockMutation = useCreateBlock();
  const updateBlockMutation = useUpdateBlock();
  const deleteBlockMutation = useDeleteBlock();

  /**
   * Handle filter changes
   */
  const handleFilterChange = (newFilters: Partial<{
    search: string;
    isActive?: boolean | undefined;
    startDate?: string | undefined;
    endDate?: string | undefined;
    sortBy: 'name' | 'startDate' | 'endDate' | 'createdAt' | 'updatedAt' | 'isActive';
    sortOrder: 'asc' | 'desc';
  }>) => {
    // Convert BlockList filters to our internal filter format
    const convertedFilters: Partial<{
      search: string;
      status: "all" | "active" | "inactive";
      sortBy: "name" | "startDate" | "endDate" | "createdAt";
      sortOrder: "asc" | "desc";
      page: number;
      limit: number;
    }> = {
      page: 1,
      limit: 20
    };

    if (newFilters.search !== undefined) {
      convertedFilters.search = newFilters.search;
    }
    if (newFilters.sortBy !== undefined) {
      convertedFilters.sortBy = newFilters.sortBy as any;
    }
    if (newFilters.sortOrder !== undefined) {
      convertedFilters.sortOrder = newFilters.sortOrder;
    }

    if (newFilters.isActive !== undefined) {
      convertedFilters.status = newFilters.isActive ? 'active' : 'inactive';
    }

    setFilters(prev => ({ ...prev, ...convertedFilters }));
  };

  /**
   * Handle create block
   */
  const handleCreateBlock = (blockData: any) => {
    createBlockMutation.mutate(blockData, {
      onSuccess: () => {
        setShowForm(false);
        setSelectedBlock(null);
      },
    });
  };

  /**
   * Handle update block
   */
  const handleUpdateBlock = (blockData: any) => {
    if (selectedBlock) {
      updateBlockMutation.mutate(
        { id: selectedBlock.id, data: blockData },
        {
          onSuccess: () => {
            setShowForm(false);
            setSelectedBlock(null);
          },
        }
      );
    }
  };

  /**
   * Handle delete block
   */
  const handleDeleteBlock = (blockId: string) => {
    deleteBlockMutation.mutate(blockId);
  };


  /**
   * Handle select block for editing
   */
  const handleSelectBlock = (block: any) => {
    setSelectedBlock(block);
    setShowForm(true);
  };

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={1} size="h2">
              Block Management
            </Title>
            <Text color="dimmed" size="sm">
              Manage time blocks for scheduling periods
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setSelectedBlock(null);
              setShowForm(true);
            }}
            loading={createBlockMutation.isPending}
          >
            Create Block
          </Button>
        </Group>

        {/* Error Alert */}
        {(blocksError || statsError) && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="Error Loading Data"
            color="red"
            variant="light"
          >
            {blocksError?.message || statsError?.message || 'An error occurred while loading data'}
          </Alert>
        )}

        {/* Main Content */}
        <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'overview')}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconInfoCircle size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="list" leftSection={<IconList size={16} />}>
              All Blocks
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <Stack gap="md">
              {/* Statistics */}
              <BlockStats
                stats={statsData as any}
                isLoading={statsLoading}
                error={statsError as any}
              />

              {/* Recent Blocks */}
              <Paper p="md" withBorder>
                <Title order={3} size="h4" mb="md">
                  Recent Blocks
                </Title>
        <BlockList
          blocks={blocks?.slice(0, 5) || []}
          isLoading={blocksLoading}
          error={blocksError}
          onSelectBlock={handleSelectBlock}
          onDeleteBlock={handleDeleteBlock}
          onUpdateBlock={handleUpdateBlock}
          filters={{
            search: '',
            isActive: undefined,
            startDate: undefined,
            endDate: undefined,
            sortBy: 'createdAt',
            sortOrder: 'desc'
          }}
          onFilterChange={() => {}}
        />
              </Paper>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="list" pt="md">
        <BlockList
          blocks={blocks || []}
          isLoading={blocksLoading}
          error={blocksError}
          onSelectBlock={handleSelectBlock}
          onDeleteBlock={handleDeleteBlock}
          onUpdateBlock={handleUpdateBlock}
          onFilterChange={handleFilterChange}
          filters={{
            search: filters.search,
            isActive: filters.status === 'active' ? true : filters.status === 'inactive' ? false : undefined,
            startDate: undefined,
            endDate: undefined,
            sortBy: filters.sortBy as any,
            sortOrder: filters.sortOrder
          }}
          pagination={blocksData?.data?.pagination}
        />
          </Tabs.Panel>
        </Tabs>

        {/* Block Form Modal */}
        <Modal
          opened={showForm}
          onClose={() => {
            setShowForm(false);
            setSelectedBlock(null);
          }}
          title={selectedBlock ? 'Edit Block' : 'Create New Block'}
          size="lg"
          centered
        >
          <BlockForm
            block={selectedBlock}
            onSubmit={selectedBlock ? handleUpdateBlock : handleCreateBlock}
            onCancel={() => {
              setShowForm(false);
              setSelectedBlock(null);
            }}
            isLoading={createBlockMutation.isPending}
          />
        </Modal>
      </Stack>
    </Container>
  );
};

export default BlockManagement;
